import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatDateForInput } from '@/utils/dateUtils';

/**
 * Shared bulk-allocate logic for the Equipment & Personnel planners.
 *
 * Owns the batch-allocate modal state and the open/confirm handlers. Selection
 * (the Set of selected ids) stays in each planner because its multi-select
 * click logic is grouping-specific, but it is passed in here.
 *
 * @param {Object}   cfg
 * @param {'equipment'|'personnel'} cfg.resource
 * @param {Array}    cfg.items            full list of items (equipment/personnel)
 * @param {Function} cfg.checkRequirement (item, project) => { required: boolean }
 * @param {Function} cfg.createAllocation async (id, item, { projectId, startDate, endDate, isOverride }) => void
 * @param {Function} cfg.onAllocated      called after a successful batch (e.g. refetch)
 * @param {Function} cfg.clearSelection   called after a batch to clear selection
 * @param {Object}   cfg.notification     useNotification() instance
 */
export function useBulkAllocate({ resource, items, checkRequirement, createAllocation, onAllocated, clearSelection, notification }) {
  const [modal, setModal] = useState(null); // { droppedIds, project, pickProject, startDate, endDate }

  const openForSelected = (selectedIds, selectedProject) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const project = selectedProject || null; // prefill if a project is selected, else pick in modal
    const baseStart = project && new Date(project.start_date) > new Date() ? project.start_date : new Date().toISOString();
    const baseEnd = project && new Date(project.end_date) > new Date() ? project.end_date : new Date(Date.now() + 30 * 86400000).toISOString();
    setModal({
      droppedIds: ids,
      project,
      pickProject: true,
      startDate: formatDateForInput(baseStart),
      endDate: formatDateForInput(baseEnd),
    });
  };

  const handleConfirm = async () => {
    if (!modal) return;
    const { droppedIds, project, startDate, endDate } = modal;
    if (!project) { notification.error('Please select a project'); return; }
    if (!startDate || !endDate) { notification.error('Start and End dates are required'); return; }
    if (new Date(endDate) <= new Date(startDate)) { notification.error('End date must be after start date'); return; }
    let created = 0;
    for (const id of droppedIds) {
      const item = items.find(i => i.id === id);
      const needsOverride = item ? !checkRequirement(item, project).required : false;
      try {
        await createAllocation(id, item, {
          projectId: project.id,
          startDate: new Date(startDate).toISOString(),
          endDate: new Date(endDate).toISOString(),
          isOverride: needsOverride,
        });
        created++;
      } catch (err) { console.warn('Allocation skip:', id, err?.response?.data?.detail); }
    }
    if (created > 0) {
      notification.success(`${created} ${resource === 'equipment' ? 'equipment' : 'personnel'} allocated to ${project.name}`);
      onAllocated();
    } else {
      notification.error('No allocations created — items may already be allocated');
    }
    clearSelection();
    setModal(null);
  };

  return { modal, setModal, openForSelected, handleConfirm };
}

/** Floating bottom-right bulk-allocate action bar. */
export const BulkAllocateBar = ({ count, testidPrefix, onAllocate, onClear }) => {
  if (!count) return null;
  return (
    <div className="fixed bottom-5 right-5 z-40 flex items-center gap-3 rounded-full bg-slate-900 text-white shadow-2xl px-4 py-2.5 border border-slate-700" data-testid={`${testidPrefix}-bulk-allocate-bar`}>
      <span className="text-sm font-medium" data-testid={`${testidPrefix}-bulk-selected-count`}>{count} selected</span>
      <Button
        size="sm"
        onClick={onAllocate}
        data-testid={`${testidPrefix}-bulk-allocate-button`}
        className="h-8 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white"
      >
        Allocate Selected ({count})
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={onClear}
        data-testid={`${testidPrefix}-bulk-clear-button`}
        className="h-8 rounded-full text-slate-300 hover:text-white hover:bg-slate-800"
      >
        Clear
      </Button>
    </div>
  );
};

/** Batch allocate modal — pick project (when pickProject) + start/end dates. */
export const BatchAllocateModal = ({ modal, setModal, projects, onConfirm, entityLabel, itemNoun, itemNounCap }) => {
  if (!modal) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setModal(null)}>
      <div className="bg-white rounded-lg shadow-xl p-6 w-96" onClick={(e) => e.stopPropagation()} data-testid="batch-drop-modal">
        <h3 className="text-lg font-semibold text-slate-900 mb-1">Batch Allocate {entityLabel}</h3>
        <p className="text-sm text-slate-500 mb-4">
          Allocating <span className="font-medium text-slate-700">{modal.droppedIds.length} {itemNoun}</span>{modal.project ? <> to <span className="font-medium text-slate-700">{modal.project.name}</span></> : ''}
        </p>
        <div className="space-y-3">
          {modal.pickProject && (
            <div>
              <Label className="text-sm font-semibold text-slate-700">Project *</Label>
              <select
                value={modal.project?.id || ''}
                onChange={(e) => {
                  const proj = projects.find(p => p.id === e.target.value) || null;
                  const ds = proj && new Date(proj.start_date) > new Date() ? proj.start_date : new Date().toISOString();
                  const de = proj && new Date(proj.end_date) > new Date() ? proj.end_date : new Date(Date.now() + 30 * 86400000).toISOString();
                  setModal({ ...modal, project: proj, startDate: formatDateForInput(ds), endDate: formatDateForInput(de) });
                }}
                data-testid="batch-project-select"
                className="mt-1 w-full h-9 rounded-md border border-slate-300 bg-white px-3 py-1 text-sm"
              >
                <option value="">Select a project…</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name} ({p.status})</option>)}
              </select>
            </div>
          )}
          <div>
            <Label className="text-sm font-semibold text-slate-700">Start Date *</Label>
            <Input
              type="date"
              value={modal.startDate}
              onChange={(e) => setModal({ ...modal, startDate: e.target.value })}
              data-testid="batch-start-date"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-sm font-semibold text-slate-700">End Date *</Label>
            <Input
              type="date"
              value={modal.endDate}
              onChange={(e) => setModal({ ...modal, endDate: e.target.value })}
              data-testid="batch-end-date"
              className="mt-1"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setModal(null)}>Cancel</Button>
            <Button size="sm" onClick={onConfirm} disabled={!modal.project} data-testid="confirm-batch-button" className="bg-slate-900 hover:bg-slate-800 disabled:opacity-40">
              Allocate {modal.droppedIds.length} {itemNounCap}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
